import { useMemo } from 'react'
import { collection, query, where, documentId } from 'firebase/firestore'
import { db } from '../lib/firebase'
import { useBatchedSubscriptions } from './useBatchedSubscriptions'
import type { UserProfile } from '../types'

const EMPTY_PROFILES: Record<string, UserProfile> = {}

// `loading` is true while any requested profile may still arrive, so a uid
// absent from `profiles` means "not here yet" — render a skeleton row. Once
// it's false, an absent uid has no user doc and gets the text fallback.
export function useUserProfilesWithLoading(userIds: string[]): {
  profiles: Record<string, UserProfile>
  loading: boolean
} {
  const { rows, pending } = useBatchedSubscriptions<UserProfile>(
    userIds,
    batch => query(collection(db, 'users'), where(documentId(), 'in', batch)),
    d => ({ uid: d.id, ...(d.data() as Omit<UserProfile, 'uid'>) }),
  )

  const profiles = useMemo(() => {
    if (rows.length === 0) return EMPTY_PROFILES
    const merged: Record<string, UserProfile> = {}
    rows.forEach(p => {
      merged[p.uid] = p
    })
    return merged
  }, [rows])

  return { profiles, loading: pending }
}

export function useUserProfiles(userIds: string[]): Record<string, UserProfile> {
  return useUserProfilesWithLoading(userIds).profiles
}
